import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import './quotes.css';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { versionTotals } from '@app/shared/price-calc';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, fieldWritable } from '../auth/me';
import { useMe } from '../auth/useMe';
import { useIsPhone } from '../events/EventsPage';
import { daysToMilli, milliToDays } from '../pricing/units';
import { ConflictNotice } from '../ui/ConflictNotice';
import { CompareDialog } from './CompareDialog';
import { DecisionDialog } from './DecisionDialog';
import { MarkSentDialog } from './MarkSentDialog';
import { SendDialog } from './SendDialog';
import { downloadPdf, PdfPreviewDialog } from './PdfPreviewDialog';
import type { Columns } from './QuoteLineRow';
import { QuoteTable } from './QuoteTable';
import { QuoteTotals } from './QuoteTotals';
import { DeleteVersionDialog } from './DeleteVersionDialog';
import { QuoteInfo } from './QuoteInfo';
import { FrozenBanner, pricesBlocked, versionActions, withQuoteExtras } from './VersionActions';
import { AddToOfferDialog } from './offers/AddToOfferDialog';
import { GoToQuoteNoticeView, packlistRights, useQuotePacklistActions, type GoToQuoteNotice } from './GenerateMenu';
import { useCrewQuote } from '../crew/CrewQuotePrompt';
import { useQuoteCrewActions } from './CrewFill';
import { linesSignature, QuoteRulePanel } from './QuoteRulePanel';
import { blockRights } from './InsertBlockDialog';
import { SaveBlockDialog } from './SaveBlockDialog';
import { VersionBar } from './VersionBar';
import { IssueInvoiceError, useIssueInvoice } from '../invoices/IssueInvoiceButton';
import { mayDelegate, ResponsibleDialog } from './ResponsibleDialog';
import { SuppliersDialog } from './SuppliersDialog';
import { SupplierCostDialog, SupplierCostsList } from './SupplierCosts';
import { MergeDuplicatesDialog, mergeLabel, mergeSummary } from './MergeLines';
import { parseAdd } from '../catalog/pick';
import { useUrlState } from '../nav/urlState';
import { useNavigate } from 'react-router-dom';
import {
  bpToPct,
  LINES_RESOURCE,
  pctToBp,
  QUOTES_KEY,
  QUOTES_RESOURCE,
  quoteKey,
  TOTALS_RESOURCE,
  versionKey,
  type Placed,
  type Quote,
  type QuoteLine,
  type QuoteOfEvent,
  type TransitionResult,
  type Version,
} from './types';
import { transportKmNowText } from './transport';
import type { ReplacedLine } from './AddPricedLine';
import { EVENTS_KEY, settlementBlocksInvoice, type Settlement } from '../events/labels';
import { NumberInput } from '../ui/NumberInput';

/** F30: how often the state of a queued e-mail is asked again. */
const EMAIL_POLL_MS = 5000;

/** The placement notice of an added line (owner 2026-10-23 20:25), or null. */
const placedOf = (line: QuoteLine): Placed | null => (line.placement ? { ...line.placement, lineId: line.id, lineName: line.name, lineVersion: line.version } : null);

/** Puts a changed line (or removes one) into the version held in the cache, without reading everything again. */
function withLine(v: Version, line: QuoteLine | null, removedId?: string): Version {
  return {
    ...v,
    sections: v.sections.map((s) => {
      let lines = s.lines.filter((l) => l.id !== removedId && l.id !== line?.id);
      if (line && line.sectionId === s.id) lines = [...lines, line].sort((a, b) => a.sort - b.sort);
      return { ...s, lines };
    }),
  };
}

/**
 * Version settings: the PDF's subtitle, the days of new lines and VAT. Sends only what changed. The discount is not
 * here (owner 20.10): "Rabat na urządzenia" in "ⓘ Info" sets the equipment lines and the default of new ones.
 */
function VersionSettings({ quote, version, onDone }: { quote: Quote; version: Version; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [subtitle, setSubtitle] = useState(quote.subtitle ?? '');
  const [days, setDays] = useState(milliToDays(version.defaultDaysMilli));
  const [vat, setVat] = useState(bpToPct(version.vatBp));
  const [local, setLocal] = useState('');
  const save = useMutation({
    // one request: the version's settings and the quote's subtitle are saved together or not at all
    mutationFn: async (v: { defaultDaysMilli: number; vatBp: number }) => {
      const body = {
        ...(quote.subtitle !== undefined && subtitle.trim() !== quote.subtitle ? { subtitle } : {}),
        ...(v.defaultDaysMilli !== version.defaultDaysMilli ? { defaultDaysMilli: v.defaultDaysMilli } : {}),
        ...(v.vatBp !== version.vatBp ? { vatBp: v.vatBp } : {}),
      };
      if (Object.keys(body).length > 0) await api(`/api/quotes/versions/${version.id}`, { method: 'PATCH', body: { ...body, version: version.version } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      onDone();
    },
  });
  /** What to send, or null with the reason shown. */
  function payload() {
    const fail = (why: string) => (setLocal(why), null);
    const defaultDaysMilli = daysToMilli(days);
    const vatBp = pctToBp(vat);
    if (defaultDaysMilli === null) return fail('Liczba dni od 0,5, np. 1 albo 1,5');
    if (vatBp === null) return fail('VAT od 0 do 100%, np. 23');
    setLocal('');
    return { defaultDaysMilli, vatBp };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const sent = payload();
    if (sent !== null) save.mutate(sent);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ subtitle, days, vat }, { subtitle: quote.subtitle ?? '', days: milliToDays(version.defaultDaysMilli), vat: bpToPct(version.vatBp) }),
    save: async () => {
      const sent = payload();
      if (sent === null) return false;
      await save.mutateAsync(sent);
      return true;
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <form ref={guardRef} className="q-settings" aria-label="Ustawienia wersji" onSubmit={submit}>
      {error?.status === 409 && <ConflictNotice onReload={() => void queryClient.invalidateQueries({ queryKey: QUOTES_KEY }).then(onDone)} />}
      <div className="q-settings-grid">
        {quote.subtitle !== undefined && (
          <label className="field q-wide">
            <span>Podtytuł w PDF</span>
            <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} autoComplete="off" />
          </label>
        )}
        <label className="field">
          <span>Dni dla nowych pozycji</span>
          <NumberInput value={days} onValueChange={setDays} />
        </label>
        <label className="field">
          <span>VAT (%)</span>
          <NumberInput value={vat} onValueChange={setVat} />
        </label>
      </div>
      <p className="hint">Dni dotyczą pozycji dodawanych od teraz; pozycje już dodane zmieniasz w tabeli. Rabat na urządzenia ustawiasz w „ⓘ Info”.</p>
      {(local || (error && error.status !== 409)) && (
        <p className="alert" role="alert">
          {local || (error && (fieldIssues(error)[0]?.message ?? error.message))}
        </p>
      )}
      <div className="actions">
        <button className="btn primary small" type="submit" disabled={save.isPending}>
          Zapisz ustawienia
        </button>
        <button className="btn small ghost" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/**
 * Tab "Kosztorys" on the event card: the version bar, the table (sections, lines, quick picker) and the totals.
 * One main button only when there is no quote yet ("Utwórz kosztorys"); rarer actions under "…". On a phone the
 * quote is read-only (editing is meant for the Mac).
 */
export function QuoteTab({
  eventId,
  clientName = '',
  autoCreate = false,
  onAutoCreateDone,
  onCompletePlanning,
  eventStatus,
  settlement,
  notice = null,
  onNoticeClose,
}: {
  eventId: string;
  /** What "Przejdź do kosztorysu" of the packlist did (owner 2026-10-21 23:31), shown over the quote (closes itself unless `lasting`). */
  notice?: GoToQuoteNotice | null;
  onNoticeClose?: (() => void) | undefined;
  /** Proposed as the recipient in "Oznacz jako wysłaną". */
  clientName?: string;
  autoCreate?: boolean;
  onAutoCreateDone?: (() => void) | undefined;
  /** The event is not confirmed although its quote is accepted: open its planning. */
  onCompletePlanning?: (() => void) | undefined;
  /** The event's status (absent when the role may not see it): an accepted quote of an unconfirmed event asks for planning. */
  eventStatus?: string | undefined;
  /** „Rozliczenie” (absent without the field): „bez faktury” blocks „Wystaw fakturę”. */
  settlement?: Settlement | undefined;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const phone = useIsPhone();
  const [selected, setSelected] = useState<string | null>(null);
  const navigate = useNavigate();
  const [settings, setSettings] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialog, setDialog] = useState<null | 'send' | 'sent' | 'accept' | 'reject' | 'compare' | 'preview' | 'responsible' | 'save-block' | 'suppliers' | 'supplier-cost' | 'merge' | 'offer'>(null);
  /** Review I2: why „Pobierz PDF” was refused (the server's message). */
  const [pdfError, setPdfError] = useState('');
  const tabRef = useRef<HTMLDivElement>(null);
  // "Wybierz z katalogu" (owner 2026-10-21): the catalog sends the person back with `dodaj=item:<id>` / `dodaj=dostawca:<priceId>`
  const { params, update } = useUrlState();
  const toAdd = parseAdd(params);
  const added = useRef<string | null>(null);
  const [addError, setAddError] = useState('');
  const [blockNotice, setBlockNotice] = useState('');
  const [placed, setPlaced] = useState<Placed | null>(null);
  const [previews, setPreviews] = useState<Record<string, number>>({});
  // F30: while the shown version's e-mail waits in the queue, its state is asked again now and then
  const data = useQuery({
    queryKey: quoteKey(eventId),
    queryFn: () => api<QuoteOfEvent>(`/api/quotes/events/${eventId}`),
    refetchInterval: (query) => (query.state.data?.current?.emailStatus === 'queued' ? EMAIL_POLL_MS : false),
  });
  const currentId = data.data?.current?.id ?? null;
  const shownId = selected ?? currentId;
  const other = useQuery({
    queryKey: versionKey(shownId ?? ''),
    enabled: shownId !== null && shownId !== currentId,
    queryFn: () => api<Version>(`/api/quotes/versions/${shownId}`),
    refetchInterval: (query) => (query.state.data?.emailStatus === 'queued' ? EMAIL_POLL_MS : false),
  });
  // F30 "Wyślij ponownie": the same frozen version's failed e-mail goes back to the queue
  const resend = useMutation({
    mutationFn: (versionId: string) => api<Version>(`/api/quotes/versions/${versionId}/resend`, { method: 'POST' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  const [sendPdf, setSendPdf] = useState(false);
  const create = useMutation({
    mutationFn: () => api<QuoteOfEvent>(`/api/quotes/events/${eventId}`, { method: 'POST' }),
    onSuccess: (q) => queryClient.setQueryData(quoteKey(eventId), q),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  // F34: "Wystaw fakturę" at the accepted version
  const invoicing = useIssueInvoice(eventId);
  // F27: "Przenoś zmiany ze sprzętówki" (off by default; changes of the packlist then fill the draft)
  const sync = useMutation({
    mutationFn: (v: { quote: Quote; on: boolean }) => api<Quote>(`/api/quotes/quotes/${v.quote.id}`, { method: 'PATCH', body: { syncFromPacklist: v.on, version: v.quote.version } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  const fresh = useMutation({
    mutationFn: (v: { quoteId: string; fromVersionId: string }) => api<Version>(`/api/quotes/quotes/${v.quoteId}/new-version`, { method: 'POST', body: { fromVersionId: v.fromVersionId } }),
    // the new draft is the current version
    onSuccess: () => setSelected(null),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  // opened with "Wycena" from the details: an event without a quote gets one at once (only once, only with the right)
  const autoCreated = useRef(false);
  const mayCreate = !!me && can(me, QUOTES_RESOURCE, 'create');
  useEffect(() => {
    if (!autoCreate || autoCreated.current || !data.data) return;
    autoCreated.current = true;
    // asked once: whatever happens next (created, or there was a quote already), the request is used up
    onAutoCreateDone?.();
    if (mayCreate && !data.data.quote && !data.data.heldByOther) create.mutate();
  }, [autoCreate, mayCreate, data.data, create, onAutoCreateDone]);

  const onPreview = useCallback((lineId: string, net: number | null) => {
    setPreviews((p) => {
      if (net === null ? !(lineId in p) : p[lineId] === net) return p;
      const next = { ...p };
      if (net === null) delete next[lineId];
      else next[lineId] = net;
      return next;
    });
  }, []);

  const q = data.data;
  const version = shownId === currentId ? (q?.current ?? null) : (other.data ?? null);
  const draftId = q?.current?.state === 'robocza' ? q.current.id : null;
  // review M5: another version shown — the notice of the previous one's line closes
  const shownVersionId = version?.id ?? null;
  useEffect(() => setPlaced(null), [shownVersionId]);
  useEffect(() => {
    if (!toAdd || !draftId) return;
    const key = `${toAdd.kind}:${toAdd.id}:${toAdd.sectionId}`;
    if (added.current === key) return;
    added.current = key;
    const body = toAdd.kind === 'item' ? { type: 'item', itemId: toAdd.id, sectionId: toAdd.sectionId, quantity: 1 } : { sectionId: toAdd.sectionId, priceId: toAdd.id, quantity: 1 };
    const url = toAdd.kind === 'item' ? `/api/quotes/versions/${draftId}/lines` : `/api/quotes/versions/${draftId}/supplier-lines`;
    // gear another event reserved first: „Dodaj mimo to” / „Anuluj” (owner 2026-10-24)
    withConflictConfirm((confirmConflicts) => api<QuoteLine>(url, { method: 'POST', body: { ...body, ...(confirmConflicts && toAdd.kind === 'item' ? { confirmConflicts } : {}) } }))
      .then((line) => {
        if (!line) return;
        setAddError('');
        setPlaced(placedOf(line));
      })
      .catch((e: unknown) => setAddError(e instanceof ApiError ? `Nie udało się dodać wybranej pozycji: ${fieldIssues(e)[0]?.message ?? e.message}` : 'Nie udało się dodać wybranej pozycji'))
      .finally(() => {
        update({ dodaj: null, sekcja: null });
        void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      });
  }, [toAdd, draftId, update, queryClient]);
  // F25: quote ↔ packlist ("Uzupełnij ze sprzętówki", "Utwórz sprzętówkę z kosztorysu", "Porównaj ze sprzętówką")
  const packlistLink = useQuotePacklistActions({
    me,
    eventId,
    quoteId: q?.quote?.id ?? null,
    version,
    versions: q?.versions ?? [],
    phone,
    differs: shownId === currentId && q?.packlistDiffers === true,
    onNewDraft: () => setSelected(null),
  });
  // owner 18:30 (1): the crew's difference with „Dodaj” / „Zdejmij” per function, in the same banner
  const crewQuote = useCrewQuote(eventId);
  // Obsada → kosztorys: "Obsada różni się od kosztorysu" with "Uzupełnij z obsady"; functions without a service
  const crewLink = useQuoteCrewActions({
    crewQuote,
    me,
    quoteId: q?.quote?.id ?? null,
    versions: q?.versions ?? [],
    phone,
    // re-review 4: one comparison engine — the crew's difference (`useCrewQuote`); the old `crewDiffers` is gone
    differs: shownId === currentId && crewQuote.text !== '',
    noService: [],
    onNewDraft: () => setSelected(null),
  });
  if (!me) return null;
  // F29: "Koszt" only with the cost fields AND the right to see profitability (owner: always); typed in any version
  const costCol = fieldVisible(me, LINES_RESOURCE, 'costGrosze') && can(me, 'finance.profitability', 'view');
  const cols: Columns = {
    price: fieldVisible(me, LINES_RESOURCE, 'unitPriceGrosze'),
    discount: fieldVisible(me, LINES_RESOURCE, 'discountBp'),
    net: fieldVisible(me, LINES_RESOURCE, 'netGrosze'),
    cost: costCol,
    costEditable: costCol && can(me, QUOTES_RESOURCE, 'edit') && !phone,
  };
  const editable = !!version && version.state === 'robocza' && can(me, QUOTES_RESOURCE, 'edit') && !phone;
  // owner 2026-10-21 00:32: suppliers in the quote — whoever types costs (F29), sees the suppliers' prices and may rent
  const supplierRights =
    !!cols.costEditable &&
    can(me, 'finance.profitability', 'edit') &&
    can(me, 'subrentals.prices', 'view') &&
    fieldVisible(me, 'subrentals.prices', 'priceDayGrosze') &&
    can(me, 'subrentals.rentals', 'create');

  const setCurrent = (fn: (v: Version) => Version) => {
    queryClient.setQueryData<QuoteOfEvent>(quoteKey(eventId), (old) => (old?.current ? { ...old, current: fn(old.current) } : old));
  };
  const refresh = () => void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
  // owner 2026-10-23: "Rabat na urządzenia" of a draft is set in "ⓘ Info" (rule 7a: only for a role that sees it)
  const mayDiscount = editable && cols.discount && fieldVisible(me, TOTALS_RESOURCE, 'discountBp');
  // the line is put in place at once; the totals come from the server right after
  const lineSaved = (line: QuoteLine) => {
    setCurrent((v) => withLine(v, line));
    refresh();
  };
  const lineAdded = (line: QuoteLine) => {
    lineSaved(line);
    // owner 2026-10-23 20:25: the next add closes the notice of the one before
    setPlaced(placedOf(line));
  };
  // owner 2026-10-24 16:15: the new line in the old one's place (same sort), its category note as when added
  const lineReplaced = (line: ReplacedLine) => {
    setPlaced((p) => placedOf(line) ?? (p?.lineId === line.replacedId ? null : p));
    setCurrent((v) => withLine(v, line, line.replacedId));
    onPreview(line.replacedId, null);
    refresh();
  };
  const lineDeleted = (id: string) => {
    // review M5: the notice about a line that is gone goes with it
    setPlaced((p) => (p?.lineId === id ? null : p));
    setCurrent((v) => withLine(v, null, id));
    onPreview(id, null);
    refresh();
  };

  // totals: the server's, or while a row is being typed, the same calculation over the typed amounts
  const live = Object.keys(previews).length > 0;
  let totals = version?.totals;
  if (totals && version && live) {
    const nets = version.sections.flatMap((s) => s.lines.map((l) => previews[l.id] ?? l.netGrosze ?? 0));
    try {
      totals = { ...versionTotals(nets, version.vatBp), discountsGrosze: totals.discountsGrosze };
    } catch {
      /* an amount out of range: keep the saved totals */
    }
  }

  const rights = {
    edit: can(me, QUOTES_RESOURCE, 'edit'),
    send: can(me, QUOTES_RESOURCE, 'send'),
    decide: can(me, QUOTES_RESOURCE, 'decide'),
    remove: can(me, QUOTES_RESOURCE, 'delete'),
    pdf: cols.price && cols.discount && cols.net && fieldVisible(me, TOTALS_RESOURCE, 'totals') && fieldVisible(me, TOTALS_RESOURCE, 'discountBp'),
    invoice: can(me, 'invoices.invoices', 'create'),
    invoiceBlocked: settlementBlocksInvoice(settlement),
  };
  const newVersion = () => q?.quote && version && fresh.mutate({ quoteId: q.quote.id, fromVersionId: version.id });
  const { primary, preview, printView, menu: actions } =
    q?.quote && version
      ? versionActions(version, q.versions, rights, phone, {
          settings: () => setSettings(true),
          send: () => setDialog('send'),
          markSent: () => setDialog('sent'),
          accept: () => setDialog('accept'),
          reject: () => setDialog('reject'),
          compare: () => setDialog('compare'),
          remove: () => setDeleting(true),
          // review I2: a refused download says why (the server's message)
          pdf: () => void downloadPdf(version.id).catch((e: unknown) => setPdfError(e instanceof Error ? e.message : 'Nie udało się pobrać PDF')),
          preview: () => setDialog('preview'),
          invoice: () => invoicing.start(version.id, version.number),
          printView: () => navigate(`/wydruk/kosztorys/${version.id}`),
        })
      : { primary: null, preview: null, printView: null, menu: [] };
  // before "Usuń wersję" (the dangerous one stays last)
  const removeAt = actions.findIndex((a) => a.danger);
  actions.splice(removeAt === -1 ? actions.length : removeAt, 0, ...packlistLink.menu);
  // F26: any version's section as a block (saving changes nothing in the quote)
  const blocks = blockRights(me);
  if (version && !phone && (blocks.saveQuote || blocks.saveEquipment)) {
    const at = actions.findIndex((a) => a.danger);
    actions.splice(at === -1 ? actions.length : at, 0, { label: 'Zapisz sekcję jako blok…', onSelect: () => setDialog('save-block') });
  }
  // owner 20.10 23:37: "Przekaż wycenę" and "Przenoś zmiany ze sprzętówki" under "…", not on top (only for who may use them)
  const quote = q?.quote;
  if (quote && version) {
    withQuoteExtras(actions, {
      delegate: quote.responsibleUserId !== undefined && mayDelegate(me) && !phone ? { responsible: quote.responsibleName ?? null, onSelect: () => setDialog('responsible') } : null,
      sync:
        quote.syncFromPacklist !== undefined && packlistRights(me).view && rights.edit && !phone && fieldVisible(me, LINES_RESOURCE, 'quantity')
          ? { on: quote.syncFromPacklist, enabled: !sync.isPending, onToggle: () => sync.mutate({ quote, on: !quote.syncFromPacklist }) }
          : null,
    });
  }
  // owner 2026-10-21 00:32: "Dostawcy dla kosztorysu" and "Sam koszt u dostawcy" under "…" (before the dangerous one)
  if (version && supplierRights && rights.edit) {
    const at = actions.findIndex((a) => a.danger);
    actions.splice(at === -1 ? actions.length : at, 0, { label: 'Dostawcy dla kosztorysu…', onSelect: () => setDialog('suppliers') }, { label: 'Sam koszt u dostawcy…', onSelect: () => setDialog('supplier-cost') });
  }
  // owner 2026-10-23 20:44: "Scal powtórzenia (N)…" only in an editable draft that has repeated lines, for whoever may
  // write the quantity (second review M1; N = the number of groups); it opens the list to tick
  const duplicates = editable && fieldWritable(me, LINES_RESOURCE, 'quantity') ? (version.duplicates ?? 0) : 0;
  if (version && duplicates > 0) {
    const at = actions.findIndex((a) => a.danger);
    actions.splice(at === -1 ? actions.length : at, 0, { label: mergeLabel(duplicates), onSelect: () => setDialog('merge') });
  }
  // „Oferta łączona” (owner 2026-10-30): this event's quote into a combined offer of its client
  if (version && !phone && (rights.edit || can(me, QUOTES_RESOURCE, 'create'))) {
    const at = actions.findIndex((a) => a.danger);
    actions.splice(at === -1 ? actions.length : at, 0, { label: 'Dodaj do oferty łączonej…', onSelect: () => setDialog('offer') });
  }
  // after sending or deciding: the event's status and flag may have changed too
  const transitioned = (_r: TransitionResult) => {
    setDialog(null);
    void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
    void queryClient.invalidateQueries({ queryKey: EVENTS_KEY });
  };
  const reload = () => {
    setDialog(null);
    refresh();
  };

  return (
    <div ref={tabRef} role="tabpanel" aria-label="Kosztorys" className="q-tab" data-feature-id="quotes.tab">
      {data.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {data.error && <p className="alert" role="alert">{data.error instanceof ApiError ? data.error.message : 'Nie udało się wczytać kosztorysu'}</p>}
      {notice && <GoToQuoteNoticeView notice={notice} onClose={() => onNoticeClose?.()} />}
      {q?.heldByOther && (
        <div className="q-empty-state">
          <p>Kosztorys prowadzi inna osoba.</p>
          <p className="hint">Widzisz tylko kosztorysy, które sam(a) utworzyłeś(-aś) albo które Ci przekazano. Jeśli masz pomóc w tej wycenie, poproś właściciela o przekazanie.</p>
        </div>
      )}
      {q && !q.quote && !q.heldByOther && (
        <div className="q-empty-state">
          <p>To wydarzenie nie ma jeszcze kosztorysu. Kosztorys bierze dni z terminu, schemat dni z cennika i rabat stałego klienta.</p>
          {can(me, QUOTES_RESOURCE, 'create') && (
            <button className="btn primary" type="button" disabled={create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Tworzenie…' : 'Utwórz kosztorys'}
            </button>
          )}
          {create.error && (
            <p className="alert" role="alert">
              {create.error instanceof ApiError ? create.error.message : 'Nie udało się utworzyć kosztorysu'}
            </p>
          )}
        </div>
      )}
      {q?.quote && version && (
        <>
          <VersionBar versions={q.versions} selectedId={version.id} onSelect={(id) => setSelected(id === currentId ? null : id)} primary={primary} secondary={preview} printView={printView} actions={actions} />
          <IssueInvoiceError invoicing={invoicing} />
          {pdfError && (
            <p className="alert" role="alert">
              {pdfError}
            </p>
          )}
          {primary?.disabled && primary.title && (
            <p className="hint" data-feature-id="events.settlement">
              {primary.title}
            </p>
          )}
          {version.rentalsOutside && version.rentalsOutside.length > 0 && (
            <p className="alert q-notice" role="status" data-feature-id="quotes.suppliers">
              Dopożyczenia poza obowiązującą wersją kosztorysu (odebrane lub zwrócone, więc zostają):{' '}
              {version.rentalsOutside.map((r) => `${r.name} × ${r.quantity} (${r.supplierName})`).join(', ')}. Sprawdź je w zakładce „Dopożyczenia”.
            </p>
          )}
          {version.transportKmNow !== undefined && (
            <p className="alert q-notice" role="status" data-feature-id="pricing.transport">
              {transportKmNowText(version)}
            </p>
          )}
          {addError && (
            <p className="alert" role="alert">
              {addError}
            </p>
          )}
          {sync.error && (
            <p className="alert" role="alert">
              {sync.error instanceof ApiError && sync.error.status === 409 ? 'Kosztorys zmienił się w międzyczasie — spróbuj jeszcze raz' : sync.error instanceof ApiError ? sync.error.message : 'Nie udało się zapisać „Przenoś zmiany ze sprzętówki”'}
            </p>
          )}
          {version.state !== 'robocza' && (
            <FrozenBanner
              version={version}
              versions={q.versions}
              mayCreate={rights.edit && !phone}
              busy={fresh.isPending}
              onNewVersion={newVersion}
              onOpen={(id) => setSelected(id === currentId ? null : id)}
              resend={rights.send ? { busy: resend.isPending, error: resend.error instanceof ApiError ? resend.error.message : null, onResend: () => resend.mutate(version.id) } : undefined}
            />
          )}
          {version.state === 'zaakceptowana' && (eventStatus === 'zapytanie' || eventStatus === 'wycena') && (
            <div className="alert q-notice" role="status">
              Klient zaakceptował wersję v{version.number}. Uzupełnij planowanie i zmień status na »Potwierdzone«.{' '}
              {onCompletePlanning && (
                <button className="btn small" type="button" onClick={onCompletePlanning}>
                  Uzupełnij planowanie
                </button>
              )}
            </div>
          )}
          {fresh.error && (
            <p className="alert" role="alert">
              {fresh.error instanceof ApiError ? fresh.error.message : 'Nie udało się utworzyć nowej wersji'}
            </p>
          )}
          {packlistLink.view}
          {crewLink}
          {blockNotice && (
            <p className="hint" role="status">
              {blockNotice}
            </p>
          )}
          {/* owner 2026-10-22: the facts under the title behind a small "ⓘ Info ▸" (collapsed, remembered per person) */}
          <div className="q-head">
            {q.quote.subtitle !== undefined && <p className="q-subtitle">{q.quote.subtitle}</p>}
            <QuoteInfo
              userId={me.id}
              quote={q.quote}
              version={version}
              editable={editable}
              packlistDiffers={q.packlistDiffers}
              discount={mayDiscount ? { onSet: refresh, onConflict: refresh } : undefined}
            />
          </div>
          {settings && editable && <VersionSettings quote={q.quote} version={version} onDone={() => setSettings(false)} />}
          {phone && version.state === 'robocza' && <p className="hint">Kosztorys edytuje się na komputerze; tu jest podgląd.</p>}
          {/* rules in the quote (owner 2026-10-24): what the crew rules want for the event, e.g. a console → „Realizator FOH” */}
          {version.state === 'robocza' && <QuoteRulePanel eventId={eventId} versionId={version.id} signature={linesSignature(version.sections.flatMap((s) => s.lines))} />}
          <QuoteTable
            key={version.id}
            eventId={eventId}
            version={version}
            editable={editable}
            cols={cols}
            onLineSaved={lineSaved}
            onLineAdded={lineAdded}
            onLineDeleted={lineDeleted}
            onLineReplaced={lineReplaced}
            placed={placed}
            onPlacedClose={() => setPlaced(null)}
            onPlaced={setPlaced}
            onPreview={onPreview}
            onRefresh={refresh}
            onReordered={(v) => {
              setCurrent(() => v);
              refresh();
            }}
            suppliers={supplierRights}
          />
          {totals && <QuoteTotals totals={totals} vatBp={version.vatBp} live={live} costs={costCol ? version.costs : undefined} />}
          {costCol && (version.costs?.supplierOnlyGrosze ?? 0) > 0 && <SupplierCostsList versionId={version.id} editable={supplierRights && rights.edit && !phone} />}
        </>
      )}
      {dialog === 'send' && version && <SendDialog version={version} onDone={transitioned} onClose={() => setDialog(null)} onReload={reload} onPreview={() => setSendPdf(true)} />}
      {dialog === 'send' && sendPdf && version && <PdfPreviewDialog versionId={version.id} number={version.number} onDownload={() => downloadPdf(version.id)} onClose={() => setSendPdf(false)} downloadBlocked={pricesBlocked(version)} />}
      {dialog === 'offer' && <AddToOfferDialog eventId={eventId} onClose={() => setDialog(null)} />}
      {dialog === 'sent' && version && <MarkSentDialog version={version} clientName={clientName} onDone={transitioned} onClose={() => setDialog(null)} onReload={reload} />}
      {(dialog === 'accept' || dialog === 'reject') && version && <DecisionDialog version={version} mode={dialog} onDone={transitioned} onClose={() => setDialog(null)} onReload={reload} />}
      {dialog === 'preview' && version && <PdfPreviewDialog versionId={version.id} number={version.number} onDownload={() => downloadPdf(version.id)} onClose={() => setDialog(null)} downloadBlocked={pricesBlocked(version)} />}
      {dialog === 'compare' && q && version && <CompareDialog versions={q.versions} shownId={version.id} onClose={() => setDialog(null)} />}
      {dialog === 'save-block' && version && (
        <SaveBlockDialog
          me={me}
          version={version}
          onDone={(message) => {
            setDialog(null);
            setBlockNotice(message);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'merge' && version && (
        <MergeDuplicatesDialog
          versionId={version.id}
          sections={version.sections}
          onMerged={(r) => {
            setBlockNotice(mergeSummary(r.merged, r.removed));
            refresh();
          }}
          onClose={() => {
            reload();
            // review M5: the focus goes back to "…", where the merge was started
            requestAnimationFrame(() => tabRef.current?.querySelector<HTMLButtonElement>('button[aria-label="Więcej działań kosztorysu"]')?.focus());
          }}
        />
      )}
      {dialog === 'suppliers' && q?.quote && <SuppliersDialog quoteId={q.quote.id} onClose={() => setDialog(null)} />}
      {dialog === 'supplier-cost' && version && <SupplierCostDialog versionId={version.id} onClose={() => setDialog(null)} />}
      {dialog === 'responsible' && q?.quote && <ResponsibleDialog quote={q.quote} onDone={reload} onClose={() => setDialog(null)} onReload={reload} />}
      {/* owner 2026-10-22: "Usuń wersję" — the version shown; the only one takes the quote with it */}
      {deleting && q?.quote && version && (
        <DeleteVersionDialog
          version={version}
          versions={q.versions}
          onDone={() => {
            setDeleting(false);
            setSelected(null);
          }}
          onClose={() => setDeleting(false)}
        />
      )}
    </div>
  );
}
